import { getUserId, getMerchantId } from './user.service';
import { Api } from 'src/common';
import { RequestOffer } from 'src/context/offer-context';
import { AdditionalFieldsValues } from 'src/containers/self-service/business-cash-advance/calculation/additional-fields-modal/additional-fields-modal';
import { authenticate } from 'src/services/auth-service';
import { Environment } from 'src/Environment';
import { capitalizeKeysAndAddRate, getAvailableLimit } from 'src/helpers/functional-helpers';

type BCAOffersResponse = {
  ok: boolean;
  result: RequestOffer;
  message?: string;
};

type SettlementAmount = {
  referenceDate: string;
  transferAmount: number;
  totalReceived: number;
  totalReceivedOverride: null | unknown; // You can replace 'any' with a more specific type if needed
}

export async function getBCAOffers(
  abortController: AbortController,
  amount?: string,
  term?: number,
): Promise<BCAOffersResponse> {
  const userId = getUserId();
  const merchantId = getMerchantId();
  const endpoint = `bca/offers?userId=${userId}&merchantId=${merchantId}&isDefault=${!(
    amount && term
  )}&amount=${amount ?? 3000}&term=${term ?? 7}`;

  const result = await Api.getV2<BCAOffersResponse>(endpoint, {
    signal: abortController.signal,
  });

  if (result && result?.response && result.status === 200) {
    return {
      ok: true,
      result: {
        ...result.response.result,
        offerDetails: {
          ...result.response.result.offerDetails,
          offer: capitalizeKeysAndAddRate(result.response.result.offerDetails.offer),
          AvailableLimit: getAvailableLimit(result.response.result.amountTermsMap, result.response.result.offerDetails)
        }
      },
    };
  }

  if (result.status > 300 && result.status < 500) {
    return {
      ok: false,
      result: result.response.result,
      message:
        "Uh oh! We're experiencing technical difficulties, but we're working on it. Please try again later.",
    };
  }

  throw new Error(
    `Get BCA Offers: Invalid response: ${result.status}: ${JSON.stringify(result.response)}`,
  );
}

type SubmitBCAOffer = {
  address: {
    line1: string;
    line2: string;
    suburb: string;
    city: string;
    province: string;
    country: string;
    postcode: string;
  };
  vatRegistrationNumber: string;
  merchantName: string;
  ApplicationId: string;
  accountHolderName: string;
  branchCode: string;
  bankName: string;
  accountNumber: string;
  merchantEmailId: string;
  bankAccountType: string;
  Message?: string;
};

type SubmitBCAOfferResponse = {
  ok?: boolean;
  result: SubmitBCAOffer;
  message?: string;
  status?: number;
};

export async function submitBCAOffer(
  abortController: AbortController,
  amount: number,
  optionId: string,
  additionalFieldValues: AdditionalFieldsValues,
): Promise<SubmitBCAOfferResponse> {
  const userId = getUserId();
  const merchantId = getMerchantId();
  const endpoint = `bca/submit`;

  const result = await Api.postV2<SubmitBCAOfferResponse>(endpoint, {
    signal: abortController.signal,
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      userReference: userId,
      merchantID: merchantId.toUpperCase(),
      bcaSelection: {
        amount: amount,
        optionId: optionId,
      },
      title: additionalFieldValues?.title?.value || null,
      idType: additionalFieldValues?.idType?.value || null,
      idNumber:
        additionalFieldValues?.idType?.value === '1'
          ? additionalFieldValues?.id
          : additionalFieldValues?.passport || null,
      businessType: additionalFieldValues?.businessType?.value || null,
      firstName: additionalFieldValues?.name || '',
      lastName: additionalFieldValues?.surname || '',
      ...(additionalFieldValues?.registrationNumber && {
        companyRegistrationNumber: additionalFieldValues?.registrationNumber,
      }),
    }),
  });

  if (result && result?.response && result.status === 200) {
    return {
      ok: true,
      result: result.response.result,
    };
  }

  if (result.status > 300 && result.status < 500) {
    return {
      ok: false,
      result: result.response.result,
      status: result.status,
      message:
        "Uh oh! We're experiencing technical difficulties, but we're working on it. Please try again later.",
    };
  }

  throw new Error(
    `Submit BCA Offer: Invalid response: ${result.status}: ${JSON.stringify(result.response)}`,
  );
}

type AcceptBCAOfferParams = {
  amount: string;
  applicationId: string;
  accountHolderName: string;
  branchCode: string;
  fileBase64: string;
  bankName: string;
  merchantName: string;
  merchantEmailId: string;
  bankAccountType: string;
  accountNumber: string;
};

type AcceptBCAOffer = {
  AdvanceId: number;
  Success: boolean;
  Message: string | null;
};

type AcceptBCAOfferResponse = {
  ok: boolean;
  result: AcceptBCAOffer;
  message?: string;
  status?: number;
};

export async function acceptBCAOffer(
  params: AcceptBCAOfferParams,
  isTopUpOffer = false
): Promise<AcceptBCAOfferResponse> {
  const userId = getUserId();
  const merchantId = getMerchantId();
  const endpoint = `bca/contract?isBcaTopUp=${isTopUpOffer}`;

  const byteCharacters = atob(params.fileBase64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: 'application/pdf' });
  const file = new File([blob], 'contract.pdf');

  let data = new FormData();

  data.append('userReference', userId ?? '');
  data.append('merchantId', merchantId.toUpperCase());
  data.append('cashAmount', params.amount);
  data.append('applicationId', params.applicationId);
  data.append('accountHolderName', params.accountHolderName);
  data.append('branchCode', params.branchCode);
  data.append('contract', file);
  data.append('bankName', params.bankName);
  data.append('accountNumber', params.accountNumber);
  data.append('merchantName', params.merchantName);
  data.append('merchantEmailId', params.merchantEmailId);
  data.append('bankAccountType', params.bankAccountType);

  const result = await Api.postV2<AcceptBCAOfferResponse>(endpoint, {
    body: data,
  });

  if (result && result?.response && result.status === 200) {
    return {
      ok: true,
      result: result?.response.result,
    };
  }

  if (result.status > 300 && result.status < 500) {
    return {
      ok: false,
      result: result.response.result,
      status: result.status,
      message:
        "Uh oh! We're experiencing technical difficulties, but we're working on it. Please try again later.",
    };
  }

  throw new Error(
    `Accept BCA Offer: Invalid response: ${result.status}: ${JSON.stringify(result.response)}`,
  );
}



export async function getSettlementAmount(abortController: AbortController, vpsNumber: string): Promise<number> {

  const accessToken = await authenticate();

  if (!accessToken) {
    throw new Error('Unable to get settlement amount');
  }

  const endpoint = `/bca/merchant/${vpsNumber}/settlement`

  const { response, status } = await Api.getV2<SettlementAmount>(Environment.current.privateBaseURL + endpoint, {
    signal: abortController.signal,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (status !== 200) {
    throw new Error('Unable to get settlement amount');
  }

  return response.transferAmount

}

